import React from "react";
import { Avatar, Button, Popover, Tooltip } from "antd";
import { QuestionCircleOutlined, PhoneOutlined, EyeOutlined } from "@ant-design/icons";
import { capitalize } from "lodash";
import { EAI_LOGO_GRAPHIC_ONLY } from "../../../config";
import { convertUtcToLocal } from "../../../library/utils";
import { convertToInitials } from "../../layoutComponent/layoutHelpers";
import MessageTypeLabel from "./MessageTypeLabel";
import { formatMessageType } from "../../../utils/formatters";
import PinnedMessageTag from "./PinMessages/PinnedMessageTag";
import UserHoverCard from "./PinMessages/UserHoverCard";
import { renderEmailStatus } from "../../../constant/EmailConstant";
import AiFeedbackButtons from "./AiFeedbackButtons";

/**
 * Reusable message header component for all message types (SMS, MMS, Email, Call).
 *
 * Renders: Avatar, timestamp, username/lead name, message type icon,
 * campaign tag, pinned message tag, otto reason, email status.
 *
 * Direction determines layout:
 * - TO_LEAD: content right-aligned, avatar on right
 * - FROM_LEAD: content left-aligned, avatar on left
 */
const MessageMetaInfo = ({
  // Direction & type
  direction, // "TO_LEAD" | "FROM_LEAD" | "TO_USER" | "FROM_OTTO"
  messageType, // "SMS" | "MMS" | "EMAIL" | "CALL" | "WIDGET_CHAT" etc.
  contentType, // for AI Response detection

  // Avatar
  avatarUser, // { fullName, profilePic } - for TO_LEAD avatar
  leadName, // string - for FROM_LEAD avatar initials
  showTranslationIcon = false,
  RenderTranslation,

  // Timestamp
  timestamp, // UTC date string
  timestampFormat = "h:mm A",
  timestampFullFormat = "ddd, MMM Do YYYY, h:mm A",

  // Name display
  userName, // for TO_LEAD direction
  displayName, // override name (used by calls)

  // Phone tooltip
  RenderPhone,
  phoneNumber = "",
  phoneDirection, // override direction for tooltip
  phoneLabel, // "Email" | "Call" | undefined (default message)
  phoneSentLabel, // custom label like "made to"
  phoneReceiveLabel, // custom label like "received from"
  tooltipClassName,

  // Optional features
  campaign, // { name, campaignType } | null
  isMessagePinned = false,
  messageNotesCount = 0,
  messageId,
  messageContent,
  messageUser, // user object for PinnedMessageTag and UserHoverCard
  reasonForResponse, // string | null
  hasAnyForwardOption = false,
  isAutoResponder = false, // userName === lang.autoResponderUserName

  // Email specific
  emailStatus, // string | null
  EmailDetailsPopover, // ReactNode - Popover content for email icon

  // Call specific
  showPhoneIcon = false, // true for calls - renders PhoneOutlined instead of MessageTypeLabel

  // Footer action buttons
  aiFeedbackProps, // { feedback, messageFeedback, isFeedbackSent, onFeedback }
  showSentButton = false, // show "Sent" media tracking button
  onSentClick, // callback for Sent button
  showRetry = false, // show retry button
  RetryComponent, // component to render for retry

  // Sending state (Phase 3a: lambda cutover renders the bubble optimistically
  // before the persisted Message exists; this prop fades the content + shows
  // a small "Sending…" footer until the SocketIO sent_message event arrives).
  isSending = false,

  // Children (for translation icon or extra elements)
  children,
}) => {
  const isSent = direction === "TO_LEAD";
  const isReceived = direction === "FROM_LEAD" || direction === "TO_USER";
  const isAiResponse = contentType === "AI Response To Lead";
  const hasCampaign = campaign && campaign.name && campaign.name !== "Default";
  const hasTags = hasCampaign || isMessagePinned;

  // Resolve the phone tooltip direction
  const resolvedPhoneDirection = phoneDirection || direction;

  // Build the phone tooltip content
  const PhoneTooltipContent = phoneNumber ? (
    <RenderPhone
      direction={resolvedPhoneDirection === "TO_USER" ? "FROM_LEAD" : resolvedPhoneDirection}
      messagephone={phoneNumber}
      {...(phoneLabel && { messageLabel: phoneLabel })}
      {...(phoneSentLabel && { messageSentLabel: phoneSentLabel })}
      {...(phoneReceiveLabel && { messageReceiveLabel: phoneReceiveLabel })}
      {...(tooltipClassName && { tooltipClassName })}
    />
  ) : null;

  // ---------- Avatar ----------
  const renderAvatar = () => {
    if (isSent) {
      return (
        <Avatar
          src={isAiResponse ? EAI_LOGO_GRAPHIC_ONLY : avatarUser?.profilePic || undefined}
          className={`${
            isAiResponse || avatarUser?.profilePic?.length > 0 ? "" : "bg-[#f6df9c]"
          } h-6 w-6 flex-row-centered items-center justify-center`}
        >
          <span className="text-[#64748B]">{convertToInitials(avatarUser?.fullName)}</span>
        </Avatar>
      );
    }

    // FROM_LEAD avatar
    return (
      <Avatar className="bg-error-400 flex-row-centered text-grey-200 text-[9.6px] font-[600] items-center justify-center h-6 w-6">
        {convertToInitials(leadName)}
      </Avatar>
    );
  };

  // ---------- Message Type Icon ----------
  const renderMessageTypeIcon = () => {
    if (showPhoneIcon) {
      return (
        <Tooltip title={capitalize(messageType || "")}>
          <PhoneOutlined className="text-grey-500 rotate-90" />
        </Tooltip>
      );
    }

    if (EmailDetailsPopover) {
      return (
        <Popover
          trigger={["hover"]}
          title={formatMessageType(messageType)}
          content={EmailDetailsPopover}
        >
          <span>
            <MessageTypeLabel
              messageType={messageType}
              placement="bottom"
              defaultIcon="mail"
              showTooltip={false}
              containerClassName={isSent ? "flex mt-0" : "-mb-1"}
              {...(!isSent && { iconClassName: "text-grey-500 ml-1" })}
            />
          </span>
        </Popover>
      );
    }

    return <MessageTypeLabel containerClassName="-mb-1" messageType={messageType} />;
  };

  // ---------- Username (TO_LEAD) ----------
  const renderUserName = () => {
    const nameClasses = "text-grey-700 line-clamp-1 break-all max-w-[180px] text-xs font-[600]";
    const nameWithHover = `${nameClasses} cursor-pointer hover:text-indigo-600 transition-colors`;
    const resolvedName = displayName || userName;

    if (isAutoResponder) {
      return (
        <Tooltip title={PhoneTooltipContent}>
          <span className={nameClasses}>{resolvedName}</span>
        </Tooltip>
      );
    }

    if (hasAnyForwardOption) {
      return (
        <Tooltip title={PhoneTooltipContent} placement="bottom">
          <span className={nameWithHover}>
            <UserHoverCard
              user={messageUser}
              messageContent={messageContent}
              userName={resolvedName}
              messageId={messageId}
              messageType={messageType}
              messageNotesCount={messageNotesCount}
              placement="topLeft"
            >
              <span>{resolvedName}</span>
            </UserHoverCard>
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={PhoneTooltipContent}>
        <span className={nameClasses}>{resolvedName}</span>
      </Tooltip>
    );
  };

  // ---------- Lead Name (FROM_LEAD) ----------
  const renderLeadName = () => {
    const resolvedName = displayName || leadName;
    return (
      <Tooltip title={PhoneTooltipContent}>
        <span
          title={resolvedName}
          className="text-brand-500 text-xs line-clamp-1 break-all max-w-[180px] font-[600]"
        >
          {resolvedName}
        </span>
      </Tooltip>
    );
  };

  // ---------- Timestamp ----------
  const renderTimestamp = () => {
    const dateValue = timestamp;
    return (
      <Tooltip title={convertUtcToLocal(dateValue, timestampFullFormat)}>
        <span className="text-xs font-[400] text-grey-500">
          {convertUtcToLocal(dateValue, timestampFormat)}
        </span>
      </Tooltip>
    );
  };

  // ---------- Reason for Response ----------
  const renderReasonForResponse = () => {
    if (!reasonForResponse) return null;

    const icon = (
      <Tooltip
        title={reasonForResponse}
        overlayInnerStyle={{
          width: "auto",
          minWidth: "min(480px, 90vw)",
          maxWidth: "min(720px, 90vw)",
          maxHeight: "min(420px, 60vh)",
          overflowY: "auto",
          padding: "10px 14px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        <span style={{ display: "inline-flex" }}>
          <QuestionCircleOutlined className="text-grey-500" />
        </span>
      </Tooltip>
    );

    const label = (
      <span className="text-xs font-[400] text-grey-500 hidden group-hover/reason:inline">
        Otto responded
      </span>
    );

    return (
      <span className="group/reason inline-flex items-center gap-1">
        {isSent ? (
          <>
            {label}
            {icon}
          </>
        ) : (
          <>
            {icon}
            {label}
          </>
        )}
      </span>
    );
  };

  // ---------- Tags Row (Campaign + Pinned) ----------
  const renderTags = () => {
    if (!hasTags) return null;
    return (
      <div className="flex items-center gap-2">
        {hasCampaign && (
          <span className="py-[2px] px-[10px] bg-orange-500 rounded-[6px] text-[12px] whitespace-nowrap">
            {campaign.name}
          </span>
        )}
        {isMessagePinned && (
          <PinnedMessageTag
            messageNotesCount={messageNotesCount}
            messageId={messageId}
            messageContent={messageContent}
            messageType={messageType}
            user={messageUser}
          />
        )}
      </div>
    );
  };

  // ========== RENDER ==========
  return (
    <div className="flex items-start">
      {/* Avatar LEFT (FROM_LEAD) */}
      {isReceived && (
        <div className="msg-avatar-left">
          {renderAvatar()}
          {showTranslationIcon && RenderTranslation && <RenderTranslation />}
        </div>
      )}

      {/* Content column */}
      <div className={isSent ? "msg-content-col-end" : "msg-content-col"}>
        {/* Header row */}
        <div
          className={`msg-timestamp-row flex-wrap ${
            isSent ? (hasTags ? "justify-end lg:justify-between" : "justify-end") : "justify-start"
          }`}
        >
          {/* Tags (left side for TO_LEAD) */}
          {isSent && renderTags()}

          {/* Header items */}
          <div className="msg-header-row">
            {/* FROM_LEAD order: name, timestamp, icon, [emailStatus], [reason] */}
            {isReceived && (
              <>
                {renderLeadName()}
                {renderTimestamp()}
                {renderMessageTypeIcon()}
                {emailStatus && renderEmailStatus(emailStatus)}
                {renderReasonForResponse()}
              </>
            )}

            {/* TO_LEAD order: [emailStatus], icon, timestamp, name, [reason] */}
            {isSent && (
              <>
                {renderReasonForResponse()}
                {emailStatus && renderEmailStatus(emailStatus)}
                {renderMessageTypeIcon()}
                {renderTimestamp()}
                {renderUserName()}
              </>
            )}
          </div>
        </div>

        {/* Message content (children) — faded while pending send */}
        <div
          style={
            isSending
              ? { opacity: 0.6, transition: "opacity 200ms ease-in-out" }
              : { transition: "opacity 200ms ease-in-out" }
          }
        >
          {children}
        </div>

        {/* Sending indicator: small italic note while waiting for the
            persisted Message to arrive via SocketIO sent_message event. */}
        {isSending && (
          <div className="flex items-center gap-1 text-[11px] italic text-black-45 mt-1">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{
                backgroundColor: "#9CA3AF",
                animation: "smai-pulse 1.2s ease-in-out infinite",
              }}
            />
            <span>Sending…</span>
            <style>{`
              @keyframes smai-pulse {
                0%, 100% { opacity: 0.4; }
                50%      { opacity: 1; }
              }
            `}</style>
          </div>
        )}

        {/* Footer (action buttons) */}
        {(aiFeedbackProps || showSentButton || showRetry) && (
          <div className="flex w-full items-center justify-between self-stretch">
            {aiFeedbackProps ? (
              <div className="self-start">
                <AiFeedbackButtons {...aiFeedbackProps} />
              </div>
            ) : (
              <div />
            )}
            {showSentButton || showRetry ? (
              <div className="flex items-center gap-2">
                {showSentButton && (
                  <Button
                    icon={<EyeOutlined />}
                    type="text"
                    size="small"
                    className="!text-black-45"
                    onClick={onSentClick}
                  >
                    Sent
                  </Button>
                )}
                {showRetry && RetryComponent && (
                  <span className="text-[11px] mb-[2px]">{RetryComponent}</span>
                )}
              </div>
            ) : (
              <div />
            )}
          </div>
        )}
      </div>

      {/* Avatar RIGHT (TO_LEAD) */}
      {isSent && (
        <div className="msg-avatar-right">
          {renderAvatar()}
          {showTranslationIcon && RenderTranslation && <RenderTranslation />}
        </div>
      )}
    </div>
  );
};

export default MessageMetaInfo;
